import React, { useRef, useEffect } from 'react';

interface SimpleRichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}

const SimpleRichTextEditor = React.forwardRef<HTMLDivElement, SimpleRichTextEditorProps>(
  ({ value, onChange, placeholder }, ref) => {
    const editorRef = useRef<HTMLDivElement>(null);
    const isUpdatingFromProps = useRef(false);

    // Update editor content when value prop changes (but not when user is typing)
    useEffect(() => {
      if (editorRef.current && !isUpdatingFromProps.current) {
        const currentContent = editorRef.current.innerHTML;
        if (currentContent !== value) {
          isUpdatingFromProps.current = true;
          editorRef.current.innerHTML = value;
          isUpdatingFromProps.current = false;
        }
      }
    }, [value]);

    const handleInput = () => {
      if (editorRef.current && !isUpdatingFromProps.current) {
        const newValue = editorRef.current.innerHTML;
        onChange(newValue);
      }
    };

    const formatText = (command: string, value?: string) => {
      // Focus the editor first to ensure proper selection
      editorRef.current?.focus();
      
      // Use modern approach for better list handling
      if (command === 'insertUnorderedList' || command === 'insertOrderedList') {
        try {
          // Check if we're already in a list
          const selection = window.getSelection();
          if (selection && selection.rangeCount > 0) {
            document.execCommand(command, false, value);
          }
        } catch (error) {
          console.warn('List command failed, trying alternative approach');
          // Fallback for list creation
          if (command === 'insertUnorderedList') {
            insertList('ul');
          } else if (command === 'insertOrderedList') {
            insertList('ol');
          }
        }
      } else {
        document.execCommand(command, false, value);
      }
      
      // Trigger onChange after formatting
      setTimeout(handleInput, 10);
    };

    // Alternative list insertion method
    const insertList = (listType: 'ul' | 'ol') => {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;

      const range = selection.getRangeAt(0);
      const listElement = document.createElement(listType);
      const listItem = document.createElement('li');
      
      // If there's selected text, use it as the first list item
      if (!range.collapsed) {
        const selectedContent = range.extractContents();
        listItem.appendChild(selectedContent);
      } else {
        listItem.innerHTML = '&nbsp;'; // Non-breaking space for empty list item
      }
      
      listElement.appendChild(listItem);
      range.insertNode(listElement);
      
      // Position cursor at the end of the list item
      range.setStartAfter(listItem);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      // Handle Enter key in lists
      if (e.key === 'Enter') {
        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          const listItem = range.commonAncestorContainer.parentElement?.closest('li');
          
          if (listItem) {
            e.preventDefault();
            
            // Check if the current list item is empty
            if (listItem.textContent?.trim() === '') {
              // Exit the list if the current item is empty
              const list = listItem.closest('ul, ol');
              if (list && listItem.parentNode === list) {
                const newParagraph = document.createElement('p');
                newParagraph.innerHTML = '<br>';
                list.parentNode?.insertBefore(newParagraph, list.nextSibling);
                
                // Remove the empty list item
                listItem.remove();
                
                // If the list is now empty, remove it
                if (list.children.length === 0) {
                  list.remove();
                }
                
                // Move cursor to the new paragraph
                const newRange = document.createRange();
                newRange.setStart(newParagraph, 0);
                newRange.collapse(true);
                selection.removeAllRanges();
                selection.addRange(newRange);
                
                handleInput();
                return;
              }
            }
            
            // Create a new list item
            const newListItem = document.createElement('li');
            newListItem.innerHTML = '<br>';
            listItem.parentNode?.insertBefore(newListItem, listItem.nextSibling);
            
            // Move cursor to the new list item
            const newRange = document.createRange();
            newRange.setStart(newListItem, 0);
            newRange.collapse(true);
            selection.removeAllRanges();
            selection.addRange(newRange);
            
            handleInput();
          }
        }
      }
    };

    return (
      <div className="border border-gray-300 rounded-md">
        {/* Toolbar */}
        <div className="flex items-center gap-1 p-2 border-b border-gray-200 bg-gray-50">
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => formatText('bold')}
            className="p-2 hover:bg-gray-200 rounded text-sm font-bold min-w-[32px] h-8 flex items-center justify-center"
            title="Bold"
          >
            B
          </button>
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => formatText('italic')}
            className="p-2 hover:bg-gray-200 rounded text-sm italic min-w-[32px] h-8 flex items-center justify-center"
            title="Italic"
          >
            I
          </button>
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => formatText('underline')}
            className="p-2 hover:bg-gray-200 rounded text-sm underline min-w-[32px] h-8 flex items-center justify-center"
            title="Underline"
          >
            U
          </button>
          <div className="w-px h-6 bg-gray-300 mx-1"></div>
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => formatText('insertUnorderedList')}
            className="p-2 hover:bg-gray-200 rounded text-sm min-w-[32px] h-8 flex items-center justify-center"
            title="Bullet List"
          >
            •
          </button>
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => formatText('insertOrderedList')}
            className="p-2 hover:bg-gray-200 rounded text-sm min-w-[32px] h-8 flex items-center justify-center"
            title="Numbered List"
          >
            1.
          </button>
          <div className="w-px h-6 bg-gray-300 mx-1"></div>
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => formatText('removeFormat')}
            className="p-2 hover:bg-gray-200 rounded text-xs min-w-[48px] h-8 flex items-center justify-center"
            title="Clear Formatting"
          >
            Clear
          </button>
        </div>

        {/* Editor */}
        <div
          ref={(node) => {
            editorRef.current = node;
            if (ref) {
              if (typeof ref === 'function') ref(node);
              else ref.current = node;
            }
          }}
          contentEditable
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          className="p-3 min-h-[200px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset prose max-w-none"
          style={{ 
            wordWrap: 'break-word',
            overflowWrap: 'break-word'
          }}
          data-placeholder={placeholder}
          suppressContentEditableWarning={true}
        />

        <style>{`
          [contenteditable]:empty:before {
            content: attr(data-placeholder);
            color: #9ca3af;
            pointer-events: none;
            display: block;
          }
          
          [contenteditable] {
            outline: none;
          }
          
          [contenteditable]:focus {
            outline: none;
          }

          /* Ensure lists are properly styled */
          [contenteditable] ul {
            list-style-type: disc;
            margin-left: 1.5em;
            padding-left: 0;
          }

          [contenteditable] ol {
            list-style-type: decimal;
            margin-left: 1.5em;
            padding-left: 0;
          }

          [contenteditable] li {
            margin: 0.25em 0;
            padding-left: 0.25em;
          }

          /* Fix for nested lists */
          [contenteditable] ul ul,
          [contenteditable] ol ol,
          [contenteditable] ul ol,
          [contenteditable] ol ul {
            margin-top: 0;
            margin-bottom: 0;
          }
        `}</style>
      </div>
    );
  }
);

SimpleRichTextEditor.displayName = 'SimpleRichTextEditor';

export default SimpleRichTextEditor;